/** @file Resolves an impostor's facet entries to per-tile sheet rects and pixel pivots (17.1). */
import type { ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import type { FacetEntry, SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { cellRectOf } from '@zilo/pose3d-export/cells/cellRectOf';
import { entryTiles } from '@zilo/pose3d-export/cells/entryTiles';
import { tableOf } from '@zilo/pose3d-export/resolve/tableOf';
import type { CellRect, ResolvedEntry } from '@zilo/pose3d-export/types/bundle';
import type { PackedBlocks } from '@zilo/pose3d-export/types/packBlock';

function emptySlot(drawable: string, path: string, slot: string): Warning {
  return {
    code: 'EMPTY_SLOT', path, params: { slot },
    message: `slot ${slot} of drawable '${drawable}' has no image; it draws nothing`,
    hint: `fill it with slot.fill { slot: '${slot}' }`,
  };
}

function resolveEntry(entry: FacetEntry, tiles: number, at: PackedBlocks['origins'][string] | undefined): ResolvedEntry {
  const strip = at === undefined ? entry : { origin: at.origin, size: entry.size, extendX: entry.extendX };
  const rects: (CellRect | null)[] = Array.from({ length: tiles }, (_, t) => (entry.slots[String(t)] === undefined ? null : cellRectOf(strip, t)));
  const pivotPx: [number, number] = [entry.pivotFrac[0] * entry.size[0], entry.pivotFrac[1] * entry.size[1]];
  const out: ResolvedEntry & Partial<Pick<FacetEntry, 'slots' | 'origin'>> = { ...entry, sheet: at?.sheet ?? entry.sheet, rects, pivotPx };
  delete out.slots;
  delete out.origin;
  return out;
}

/**
 * The entries of `impostor` (drawable `drawableIndex`): a packed entry's tiles sit in its block on the packed sheet
 * (`origins['<drawable>/<entry>']`), a sheet-backed entry's tiles at their strip rect; an empty tile is `null` and
 * raises one `EMPTY_SLOT` at `/puppet/drawables/<i>/facets/<e>/slots/<t>`; `pivotPx = pivotFrac x size`.
 */
export function resolveEntries(
  puppet: Puppet, impostor: ImpostorDrawable, drawableIndex: number, tables: Readonly<Record<string, SectorTable>>, origins: PackedBlocks['origins'],
): { entries: ResolvedEntry[]; warnings: Warning[] } {
  const warnings: Warning[] = [];
  const entries = impostor.facets.map((entry, e) => {
    const path = `/puppet/drawables/${drawableIndex}/facets/${e}`;
    const tiles = entryTiles(entry, tableOf(entry.yaw, puppet, tables, `${path}/yaw`));
    for (let t = 0; t < tiles; t += 1) {
      if (entry.slots[String(t)] === undefined) warnings.push(emptySlot(impostor.id, `${path}/slots/${t}`, `${impostor.id}/${e}/${t}`));
    }
    const key = `${impostor.id}/${e}`;
    return resolveEntry(entry, tiles, Object.hasOwn(origins, key) ? origins[key] : undefined);
  });
  return { entries, warnings };
}
